import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, map, switchMapTo, take, tap } from 'rxjs/operators';
import { Instrument, Valuation, InstrumentType, BaseValuation } from './instrument.model';
import { InstrumentsStore } from './instruments.store';
import { Cacheable } from 'ts-cacheable';
import { SessionQuery } from '../../../core/state/session/session.query';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { arrayRemove, arrayUpsert, withTransaction } from '@datorama/akita';
import { PrivateAssetService } from '../../../pages/authenticated/private-asset/state/private-asset.service';
import { Transaction } from '../../../features/transactions/state/transaction.model';
import { TransactionsStore } from 'src/app/features/transactions/state/transactions.store';

@Injectable({ providedIn: 'root' })
export class InstrumentsService {
    constructor(
        private instrumentsStore: InstrumentsStore,
        private http: HttpClient,
        private config: AppConfigQuery,
        private sessionQuery: SessionQuery,
        private privateAssetService: PrivateAssetService,
        private transactionsStore: TransactionsStore
    ) {
        this.sessionQuery.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                switchMapTo(this.get())
            )
            .subscribe();
    }

    @Cacheable()
    get() {
        return this.http
            .get<Instrument[]>(`${this.config.get().gatewayBaseUrl}/instruments`)
            .pipe(
                tap((entities) => {
                    this.parseInstruments(entities, false);
                })
            );
    }

    getWithMultipleTransactions(categories: Array<string> = null) {
        return this.http
            .get<Instrument[]>(`${this.config.get().gatewayBaseUrl}/instruments`, {
                params: { category: categories }
            })
            .pipe(
                tap((entities) => {
                    this.parseInstruments(entities, true);
                })
            );
    }

    parseInstruments(
        entities: Instrument[],
        mapWithMultipleTransactions: boolean = false
    ) {
        this.instrumentsStore.add(entities);
        // Iterate across all instruments and fetch valuations
        // for instruments of type COMMERCIAL_REAL_ESTATE
        let ids: Array<number> = [];
        let idsValuation: Array<number> = [];
        entities.forEach((entity) => {
            // Todo: Collect all instrumentIds and fetch valuations in one request instead multiple requests
            // and then update the state store with the valuations
            if (entity.type === 'COMMERCIAL_REAL_ESTATE') {
                //this.getValuations(entity.instrumentId).subscribe();
                //this.getTransactions(entity.instrumentId).subscribe();
                if (!ids.includes(entity.instrumentId)) ids.push(entity.instrumentId);
                if (!idsValuation.includes(entity.instrumentId))
                    idsValuation.push(entity.instrumentId);
            }

            if (entity.type === 'LOAN') {
                //this.getTransactions(entity.instrumentId).subscribe();
                if (!ids.includes(entity.instrumentId)) ids.push(entity.instrumentId);
                if (entity.underlyingInstrumentId) {
                    /*this.getTransactions(
                        entity.underlyingInstrumentId
                    ).subscribe();*/
                    if (!ids.includes(entity.underlyingInstrumentId))
                        ids.push(entity.underlyingInstrumentId);
                }
            }

            if (entity.type === 'CASH') {
                //this.getTransactions(entity.instrumentId).subscribe();
                if (!ids.includes(entity.instrumentId)) ids.push(entity.instrumentId);
            }
        });

        this.getMultipleValuations(idsValuation).subscribe();
        // Blystad specific hotfix #SWIM-4883 for Private Equity
        if (mapWithMultipleTransactions) this.getMultipleTransactions(ids);
    }

    getById(id) {
        return this.http
            .get<Instrument>(
                `${this.config.get().gatewayBaseUrl}/instruments/single/${id}`
            )
            .pipe(tap((entity) => this.instrumentsStore.upsert(id, entity)));
    }

    add(instrument: Instrument) {
        return this.http
            .post<Instrument>(
                `${this.config.get().gatewayBaseUrl}/instruments`,
                instrument
            )
            .pipe(
                tap((entity) => {
                    this.instrumentsStore.upsert(entity.instrumentId, entity);
                    this.getValuations(entity.instrumentId).subscribe();
                    this.getTransactions(entity.instrumentId).subscribe();
                })
            );
    }

    update(id, instrument: Partial<Instrument>) {
        return this.http
            .put<Instrument>(
                `${this.config.get().gatewayBaseUrl}/instruments/single/${id}`,
                instrument
            )
            .pipe(
                tap((updatedInstrument) => {
                    this.instrumentsStore.update(
                        updatedInstrument.instrumentId,
                        updatedInstrument
                    );
                })
            );
    }

    //Created a new update that seems to work, keeping the old one in case
    /*update(id, instrument: Partial<Instrument>) {
        this.instrumentsStore.update(id, instrument);
    }*/

    /** @deprecated - use data time series store instead
     * Get valuations - returns valuations for given instrument.
     * Response is returned from cache until a new request is made using a new id.
     * @param instrumentId - instrument id
     * @returns - valuations
     */
    @Cacheable()
    getValuations(instrumentId: number) {
        return this.http
            .get<BaseValuation[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/marketdata/instrument-value?instrumentId=${instrumentId}`
            )
            .pipe(
                tap((valuations) => {
                    this.instrumentsStore.update(instrumentId, (state) => ({
                        ...state,
                        valuations
                    }));
                })
            );
    }
    /** @deprecated - use data time series store instead */
    @Cacheable()
    private getMultipleValuations(instrumentIds: Array<number>) {
        return this.http
            .get<BaseValuation[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/marketdata/instrument-value?instrumentId=${instrumentIds.join(',')}`
            )
            .pipe(
                withTransaction((valuations) => {
                    valuations.forEach((v) => {
                        this.instrumentsStore.update(v.instrumentId, (state) => ({
                            ...state,
                            valuations: [...(state.valuations || []), v]
                        }));
                    });
                })
            );
    }

    /** Add valuation - add/edit valuation for given instrument.
     * @param instrumentId - instrument id
     * @param valuation - valuation
     * @returns - observable for request
     */
    addValuation(instrumentId: number, valuation: Valuation) {
        return this.http
            .post(
                `${this.config.get().gatewayBaseUrl}/marketdata/instrument-value`,
                valuation
            )
            .pipe(
                tap((_) => {
                    this.instrumentsStore.update(instrumentId, ({ valuations }) => ({
                        valuations: arrayUpsert(valuations, valuation.id, valuation)
                    }));

                    // TODO: remove this when private asset store has been merged with instruments store
                    this.privateAssetService.getPrivateAssetsIfAuthenticated();
                    this.privateAssetService.fetchPrivateAsset(instrumentId);
                })
            );
    }

    /** @deprecated - use transactions store instead */
    @Cacheable()
    private getTransactions(instrumentId: number) {
        return this.http
            .get<any[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/view/transactions/v1?instrumentId=${instrumentId}`
            )
            .pipe(
                tap((transactions) => {
                    this.instrumentsStore.update(instrumentId, (state) => ({
                        ...state,
                        transactions
                    }));
                })
            );
    }

    /** @deprecated - use transactions store instead */
    private getMultipleTransactions(instrumentIds: Array<number>) {
        this.http
            .get<Transaction[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/view/transactions/v1?instrumentIds=${instrumentIds.join(',')}`
            )
            .pipe(
                withTransaction((transactions) => {
                    transactions.forEach((transaction) => {
                        const instrumentId = transaction.instrument.instrumentId;
                        this.instrumentsStore.update(instrumentId, (state) => ({
                            ...state,
                            transactions: this.filterOutOldTransactionsOnUpdate(
                                state.transactions,
                                transaction
                            )
                        }));
                    });
                })
            )
            .subscribe();

        this.http
            .get<Transaction[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/view/transactions/v1?underlyingInstrumentIds=${instrumentIds.join(',')}`
            )
            .pipe(
                withTransaction((transactions) => {
                    transactions.forEach((transaction) => {
                        const instrumentId = transaction.instrument.instrumentId;
                        this.instrumentsStore.update(instrumentId, (state) => ({
                            ...state,
                            transactions: this.filterOutOldTransactionsOnUpdate(
                                state.transactions,
                                transaction
                            )
                        }));
                    });
                })
            )
            .subscribe();
    }

    filterOutOldTransactionsOnUpdate(
        transactions: Transaction<any>[],
        transaction: Transaction
    ): Transaction<any>[] {
        if (!transactions) return [transaction];

        let filteredTransactions = transactions.filter(
            (previousTransaction) =>
                previousTransaction.transactionId != transaction.transactionId
        );

        filteredTransactions.push(transaction);

        return filteredTransactions;
    }

    /** Delete valuation - delete valuation
     * @param valuation - valuation to delete
     * @returns - observable for request
     */
    deleteValuation(valuation) {
        const deletedValuation = {
            date: valuation.date,
            instrumentId: valuation.instrumentId
        };
        return this.http
            .post(
                `${this.config.get().gatewayBaseUrl}/marketdata/instrument-value`,
                deletedValuation
            )
            .pipe(
                tap((_) => {
                    this.instrumentsStore.update(
                        valuation.instrumentId,
                        ({ valuations }) => ({
                            valuations: arrayRemove(valuations, valuation.id)
                        })
                    );
                    // TODO: remove this when private asset store has been merged with instruments store
                    this.privateAssetService.fetchPrivateAsset(valuation.instrumentId);
                })
            );
    }

    /**
     * As mentioned as listed instrument types on be.
     * Previously retreived through listed-instrument-types endpoint.
     */
    listedInstrumentTypes(): InstrumentType[] {
        return [
            'EQUITY',
            'CONVERTIBLE',
            'FUND',
            'BOND',
            'INDEX',
            'CRYPTO',
            'SUBSCRIPTIONRIGHT'
        ];
    }
}
